import React from 'react'

import {
  Text,
  Tooltip,
  useComputedColorScheme,
  useMantineColorScheme,
} from '@mantine/core'
import { observer } from 'mobx-react-lite'

import { deleteTitlePage } from '#choo-app/lib/editor/plugins/titlePageView/commands'
import { Toolbar } from '#components/Toolbar'
import { showCustomizeFormatModal } from '#debug/CustomizeScriptFormat'
import { useMonkeyTyping } from '#debug/useMonkeyTyping'
import { useDebugFlag, useDebugToolbar, useFeatureFlag } from '#hooks'
import { ILoadedScript, useMst } from '#state'
import { setBlockOverrides, setPageConfig } from '#util'

import { downloadScriptFormat } from './downloadScriptFormat'
import { showPageLayoutModal } from './PageLayoutModal'

import styles from './DevToolbar.module.css'

const OverrideFormatOptions = {
  structural: {
    disabled: true,
    tooltip: 'Override script format (disabled for structural scripts)',
  },
  inline: {
    disabled: false,
    tooltip: 'Override script format',
  },
}

export const DevToolbar = observer<{
  script: ILoadedScript
}>(function DevToolbar({ script }) {
  const { user, appStatus, socketManager } = useMst()
  const debugToolbar = useDebugToolbar()
  const [splitPointsEnabled, toggleSplitPoints] = useDebugFlag('split-points')
  const [legacyPaginator, setLegacyPaginator] =
    useFeatureFlag('legacy-paginator')
  const { isTyping, toggleTyping } = useMonkeyTyping(script)
  const { setColorScheme } = useMantineColorScheme()
  const colorScheme = useComputedColorScheme('light')
  const { configData } = script.pmEditor
  const [triggerRenderError, setTriggerRenderError] = React.useState(false)

  if (!(debugToolbar.isVisible && configData)) {
    return null
  }

  if (triggerRenderError) {
    throw new Error('Intentional render error for testing error boundary')
  }
  const pageConfig = configData.currentPageConfig
  const blockFormats = configData.currentBlockFormats

  const isStructural =
    script.pmEditor.configData?.paginationType === 'structural'

  const overrideFormatOpts = isStructural
    ? OverrideFormatOptions.structural
    : OverrideFormatOptions.inline

  const monkeyTooltip = isTyping
    ? 'Click to stop monkey typing'
    : 'Click to make a monkey start typing stuff in your script'

  const connectionTooltip = appStatus.isDisconnected
    ? 'Reconnect'
    : 'Disconnect'
  const toggleConnection = () => {
    if (appStatus.isDisconnected) {
      socketManager.connect()
    } else {
      socketManager.disconnect()
    }
  }

  const isInline = configData?.paginationType === 'inline'

  function toggleColorScheme() {
    setColorScheme(colorScheme === 'dark' ? 'light' : 'dark')
  }

  return (
    <Toolbar className={styles.root}>
      <Toolbar.Section position="left">
        <Toolbar.Button
          tooltip={overrideFormatOpts.tooltip}
          icon="fa-stars"
          onClick={() => showCustomizeFormatModal(script)}
          disabled={overrideFormatOpts.disabled}
        />
        <Toolbar.Button
          tooltip={monkeyTooltip}
          icon="fa-monkey"
          onClick={toggleTyping}
          active={isTyping}
          disabled={!script.isEditable}
        />
        <Toolbar.Button
          tooltip={connectionTooltip}
          icon="fa-signal-slash"
          onClick={toggleConnection}
          active={appStatus.isDisconnected}
        />
        <Toolbar.Button
          tooltip="Download custom script format as JSON file"
          icon="fa-text-size"
          disabled={isStructural}
          onClick={() => downloadScriptFormat(script)}
        />
        <Toolbar.Button
          tooltip="Reset What's New announcement status"
          icon="fa-bullhorn"
          onClick={user.resetAnnouncement}
        />
        <Toolbar.Button
          tooltip="Trigger an uncaught error (event handler)"
          icon="fa-bomb"
          onClick={() => {
            throw new Error('whoops!')
          }}
        />
        <Toolbar.Button
          tooltip="Trigger a rendering error (will be caught by error boundary)"
          icon="fa-skull-crossbones"
          onClick={() => setTriggerRenderError(true)}
        />
        <Toolbar.Button
          tooltip="Customize page size and margins"
          icon="fa-memo-pad fa-regular"
          disabled={!pageConfig}
          onClick={() => {
            if (!pageConfig) return

            showPageLayoutModal({
              pageConfig,
              blockFormats,
              onConfirm: ({ pageConfig, blockOverrides }) => {
                const { editorView } = script.pmEditor
                if (editorView) {
                  if (blockOverrides) {
                    setBlockOverrides(editorView, blockOverrides)
                  }
                  setPageConfig(editorView, pageConfig)
                }
              },
            })
          }}
        />
        <Toolbar.Button
          tooltip="Show page and block margins"
          icon="fa-square-dashed fa-regular fa-sharp"
          active={script.showLayoutLines}
          onToggle={script.setShowLayoutLines}
        />
        <Toolbar.Button
          tooltip="Visualize linebreak split points"
          icon="fa-arrow-turn-down fa-regular fa-solid"
          active={splitPointsEnabled}
          onToggle={toggleSplitPoints}
        />
        <Toolbar.EditorButton
          tooltip="Corrupt revisions"
          aria-description="Make the revisions for this document unsupported"
          icon="fa-shredder"
          command={function clearRevisions(state, dispatch) {
            if (dispatch) {
              dispatch(
                state.tr.setDocAttribute('revisions', { error: 'kaboom!' }),
              )
            }
            return true
          }}
        />
        <Toolbar.EditorButton
          tooltip="Clear revisions"
          icon="fa-align-slash"
          command={function clearRevisions(state, dispatch) {
            if (state.doc.attrs.revisions === null) return false
            if (dispatch) {
              dispatch(state.tr.setDocAttribute('revisions', null))
            }
            return true
          }}
        />
        <Toolbar.EditorButton
          tooltip="Delete title page"
          icon="fa-file-slash"
          command={deleteTitlePage}
        />
        <Toolbar.Button
          tooltip={
            legacyPaginator
              ? 'Turn on the new pagination implementation'
              : 'Go back to the old pagination implementation'
          }
          icon="fa-book-open"
          active={!legacyPaginator}
          onToggle={(next) => setLegacyPaginator(!next)}
        />
        <Toolbar.Button
          tooltip="Toggle dark mode (ctrl+j)"
          icon="fa-moon"
          active={colorScheme === 'dark'}
          onClick={toggleColorScheme}
        />
      </Toolbar.Section>
      <Toolbar.Section position="right">
        {!isInline && (
          <Tooltip position="bottom" label="Current script pagination type">
            <Text size="sm" fw="bold" c="red" pr={10}>
              {script.pmEditor.configData?.paginationType}
            </Text>
          </Tooltip>
        )}
        <Toolbar.Button
          tooltip="Hide debug toolbar"
          icon="fa-xmark"
          onClick={debugToolbar.hide}
        />
      </Toolbar.Section>
    </Toolbar>
  )
})
